import { OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type GeoLocation } from "@src/appV2/Location";
import {
  type ClaimShiftRequest,
  useClaimShift,
} from "@src/appV2/OpenShifts/ShiftAction/api/useClaimShift";
import { useShiftDiscoverySearchMode } from "@src/appV2/redesign/ShiftDiscovery/useShiftDiscoverySearchMode";
import { useCallback, useState } from "react";

import { APP_V2_APP_EVENTS, logError } from "../../../../lib/analytics";

interface FailedShift {
  shiftId: string;
  error: unknown;
}

interface BookBundleShiftsResult {
  bookedShiftIds: string[];
  failedShifts: FailedShift[];
}

export function useBookBundleShifts(bundleShifts: OpenShift[], offersIdMap: Map<string, string>) {
  const { mutateAsync: claimShift } = useClaimShift();
  const searchMode = useShiftDiscoverySearchMode();
  const [isBooking, setIsBooking] = useState(false);

  const bookBundleShifts = useCallback(
    async (
      selectedShiftIds: Set<string>,
      geoLocation?: GeoLocation
    ): Promise<BookBundleShiftsResult> => {
      setIsBooking(true);

      try {
        const shiftsToBook = bundleShifts.filter((shift) => selectedShiftIds.has(shift.id));

        const claimRequests: ClaimShiftRequest[] = [];

        const failedShifts: FailedShift[] = [];
        const bookedShiftIds: string[] = [];

        for (const shift of shiftsToBook) {
          const offerId = offersIdMap.get(shift.id);

          if (isDefined(offerId)) {
            claimRequests.push({
              shiftId: shift.id,
              offerId,
              searchMode,
              latitude: geoLocation?.latitude,
              longitude: geoLocation?.longitude,
            });
          } else {
            logError(APP_V2_APP_EVENTS.SHIFT_BUNDLE_NO_OFFER_FOUND_FAILURE, {
              error: new Error("No offer found for shift"),
              metadata: {
                shiftId: shift.id,
                offerId,
              },
            });
            failedShifts.push({
              shiftId: shift.id,
              error: "No offer found for shift",
            });
          }
        }

        const results = await Promise.allSettled(
          claimRequests.map(async (request) => {
            await claimShift(request);
            return request.shiftId;
          })
        );

        for (const [index, result] of results.entries()) {
          if (result.status === "fulfilled") {
            bookedShiftIds.push(result.value);
          } else {
            failedShifts.push({
              shiftId: claimRequests[index].shiftId,
              error: result.reason,
            });
          }
        }

        return { bookedShiftIds, failedShifts };
      } finally {
        setIsBooking(false);
      }
    },
    [bundleShifts, claimShift, searchMode, offersIdMap]
  );

  return {
    bookBundleShifts,
    isBooking,
  };
}
